import React, { createContext, useContext, useEffect, useState } from "react";
import { getPublishedSelfEnrollment } from "_graphql/queries";
import { generateFlow } from "./_helpers/generateFlow";
import { loadFont } from "./_helpers/fontLoader";
import queryString from "query-string";
import TagManager from "react-gtm-module";
const SettingsContext = createContext();

// Create a provider component
export const SettingsProvider = ({ children, client }) => {
  const [settings, setSettings] = useState(null);
  const [flow, setFlow] = useState([null]);
  const [isPreview, setIsPreview] = useState(false);

  // Load Payments
  const fetchSettings = async () => {
    console.log("Hostname:", window.location.hostname);
    const queryData = await client.query({
      variables: {
        company_id: null,
        domain: window.location.hostname,
      },
      query: getPublishedSelfEnrollment,
    });
    const enrollmentData = queryData?.data?.getPublishedSelfEnrollment;
    if (enrollmentData?.company_id) {
      localStorage.setItem('pp_company_id', enrollmentData?.company_id);
      delete enrollmentData.company_id;
    }
    const flow = generateFlow(enrollmentData);
    setFlow(flow);
    await updateSettings(enrollmentData);
  };

  const getNextStep = (currentStep) => {
    const currentStepIndex = flow.findIndex(
      (step) => step.name === currentStep
    );
    return flow[currentStepIndex + 1] || null;
  };

  const handleMessage = async (event) => {
    if (event.data?.type === "PAYPATH_SETTINGS" && event?.data?.settings) {
      // console.log("NEW_SETTINGS", event.data.settings);
      await updateSettings(event.data.settings);
      const flow = generateFlow(event.data.settings);
      setFlow(flow);
    }
  };

  useEffect(() => {
    const params = queryString.parse(window.location.search);
    if (params?.preview === "paypath") {
      setIsPreview(true);
      window.addEventListener("message", handleMessage);
      return () => {
        window.removeEventListener("message", handleMessage);
      };
    } else {
      fetchSettings();
    }
  }, []);

  const updateSettings = async (newSettings) => {
    // Don't update settings at all until fonts are loaded
    const fontPromises = [];

    if (
      newSettings.primary_font &&
      newSettings.primary_font !== settings?.primary_font
    ) {
      fontPromises.push(loadFont(newSettings.primary_font, "primary-font"));
    }

    if (
      newSettings.secondary_font &&
      newSettings.secondary_font !== settings?.secondary_font
    ) {
      fontPromises.push(loadFont(newSettings.secondary_font, "secondary-font"));
    }

    // Wait for all fonts to load before updating settings
    await Promise.all(fontPromises);

    // Initialize Google Tag Manager if gtm_id is present and different
    if (newSettings?.gtm_id && newSettings.gtm_id !== settings?.gtm_id) {
      TagManager.initialize({
        gtmId: newSettings.gtm_id,
      });
    }

    // Only now update settings, when fonts are ready
    setSettings(newSettings);
  };

  // Update page title when settings change
  useEffect(() => {
    const title = settings?.meta_title || settings?.title;
    if (title) {
      document.title = title;
    }
  }, [settings?.meta_title, settings?.title]);

  // Update meta description when settings change
  useEffect(() => {
    if (settings?.meta_description) {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement("meta");
        metaDesc.name = "description";
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = settings.meta_description;
    }
  }, [settings?.meta_description]);

  // Update canonical URL when settings change
  useEffect(() => {
    if (settings?.canonical_url) {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement("link");
        canonicalLink.rel = "canonical";
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.href = settings.canonical_url;
    }
  }, [settings?.canonical_url]);

  // Update favicon when settings change
  useEffect(() => {
    if (settings?.favicon_url) {
      // Find existing favicon link or create a new one
      let faviconLink = document.querySelector("link[rel='icon']") || 
                        document.querySelector("link[rel='shortcut icon']");
      
      if (!faviconLink) {
        faviconLink = document.createElement("link");
        faviconLink.rel = "icon";
        document.head.appendChild(faviconLink);
      }
      
      faviconLink.href = settings.favicon_url;
    }
  }, [settings?.favicon_url]);

  const getCurrentStep = () => {
    const route = window.location.pathname?.replace("/", "");
    if (route === "") return flow[0];
    return flow.find((step) => step.name === route) || null;
  };

  const getProgress = () => {
    const route = window.location.pathname?.replace("/", "");
    if (route === "") {
      return (1 / flow.length) * 100;
    }
    let currentStepIndex = flow.findIndex((step) => step.name === route);
    if (currentStepIndex === -1) {
      return null;
    }
    currentStepIndex++;
    const totalSteps = flow.length;
    return (currentStepIndex / totalSteps) * 100;
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        flow,
        getNextStep,
        isPreview,
        getProgress,
        getCurrentStep,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

// Custom hook to use the context
export const useSettings = () => {
  return useContext(SettingsContext);
};
